import { createSelector, createStructuredSelector } from "reselect"
import Immutable from "immutable"
import {
    selectSpecificCustomDescription,
    makeSelectFieldFromCustomDescription,
} from "shared/state_management/selectors"
import {
    selectIsLimitedToTeamsEnabled,
    selectOUtboxDomainsForComponent,
    selectSingleOutboxDomain,
} from "shared/userdata/userdata-selectors"
import {
    createIdsFromResourceSelector,
    createToJsSelector,
    idFromResourceUri,
    parseDateTime,
} from "shared/imports/sharedHelperFunctions"
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { selectCustomFields } from "shared/customFields/selectors"
import { targetPlaceholderRegex } from "shared/imports/regex"
import { DataItem } from "app/static/frontend/profiles-new/containers/shared/DataItem"

export const getCampaignState = (state) => {
    return state.campaign
}

export const campaignStateSelectCreator = (key) => createSelector(getCampaignState, (state) => state.get(key))

// Props needed from campaign store for CampaignForm
const getCampaign = campaignStateSelectCreator("campaign")
const editingCampaign = campaignStateSelectCreator("editingCampaign")
const postSubmissionAction = campaignStateSelectCreator("postSubmissionAction")
const limitsParticipants = campaignStateSelectCreator("limitsParticipants")
const loadingCampaign = campaignStateSelectCreator("loadingCampaign")
const sameDraft = campaignStateSelectCreator("sameDraft")
const hasCustomTarget = campaignStateSelectCreator("hasCustomTarget")
const campaignStatusFilter = campaignStateSelectCreator("campaignStatusFilter")

export const campaignSelectCreator = (key) => createSelector(getCampaign, (campaign) => campaign && campaign.get(key))

// Props needed from campaign for CampaignForm
export const actionCenter = campaignSelectCreator("action_center")
export const activated = campaignSelectCreator("activated")
export const allowSupportersToTargetAllTargets = campaignSelectCreator("allow_all_supporters_to_target_all_targets")
export const alwaysShowToAdvocates = campaignSelectCreator("always_show_to_advocates")
export const backgroundColorOverride = campaignSelectCreator("background_color_override")
export const backgroundColorPickerState = campaignSelectCreator("backgroundColorPickerState")
export const backgroundImageOverrideUrl = campaignSelectCreator("background_image_override_url")
export const backgroundStyleType = campaignSelectCreator("background_style_type")
export const campaignType = campaignSelectCreator("campaign_type")
export const draftMessage = campaignSelectCreator("draft_message")
export const draftRequirements = campaignSelectCreator("draft_requirements")
export const commentOrganizationType = campaignSelectCreator("comment_organization_type")
export const submittingOrganizationName = campaignSelectCreator("submitting_organization_name")
export const fixedPreMessage = campaignSelectCreator("fixed_pre_message")
export const fixedPostMessage = campaignSelectCreator("fixed_post_message")
export const fixedPreMessageTwo = campaignSelectCreator("fixed_pre_message_two")
export const fixedPostMessageTwo = campaignSelectCreator("fixed_post_message_two")
export const footerHtml = campaignSelectCreator("footer_html")
export const fullBleedHeaderImageContain = campaignSelectCreator("full_bleed_header_image_contain")
export const fullBleedHeaderImageUrl = campaignSelectCreator("full_bleed_header_image_url")
export const fullBleedHeaderVideoUrl = campaignSelectCreator("full_bleed_header_video_url")
export const fullBleedHeaderType = campaignSelectCreator("full_bleed_header_type")
export const id = campaignSelectCreator("id")
export const isP2A = campaignSelectCreator("is_p2a")
export const internalOnlyName = campaignSelectCreator("internal_only_name")
export const isFullBleedHeaderTextLeftAligned = campaignSelectCreator("full_bleed_header_text_is_left_aligned")
export const isOneClickRegistrationEnabled = campaignSelectCreator("is_one_click_registration_enabled")
export const longDescription = campaignSelectCreator("long_description")
export const multipleActionsAllowed = campaignSelectCreator("multiple_actions_allowed")
export const campaignSharingAllowed = campaignSelectCreator("allow_sharing")
export const name = campaignSelectCreator("name")
export const navigationBarIsTransparent = campaignSelectCreator("navigation_bar_is_transparent")
export const navigationBarType = campaignSelectCreator("navigation_bar_type")
export const negMessage = campaignSelectCreator("neg_message")
export const negSubject = campaignSelectCreator("neg_subject")
export const pageLayout = campaignSelectCreator("page_layout_type")
export const descriptionAlignment = campaignSelectCreator("description_alignment_type")
export const posMessage = campaignSelectCreator("pos_message")
export const posSubject = campaignSelectCreator("pos_subject")
export const multiMessages = campaignSelectCreator("multi_messages")
export const singleBodyForAllMessages = campaignSelectCreator("single_body_for_all_messages")
export const supportersCanChooseTargets = campaignSelectCreator("supporters_can_choose_targets")
export const priority = campaignSelectCreator("order")
export const registrationPage = campaignSelectCreator("registration_page")
export const regulationId = campaignSelectCreator("regulation_id")
export const requireConfirmation = campaignSelectCreator("require_confirmation")
export const shareUrl = campaignSelectCreator("share_url")
export const shortDescription = campaignSelectCreator("short_description")
export const shouldUseFooterHtml = campaignSelectCreator("should_use_footer_html")
export const showNumActions = campaignSelectCreator("show_num_actions")
export const showNumSupporters = campaignSelectCreator("show_num_supporters")
export const status = campaignSelectCreator("status")
export const successRedirectUrl = campaignSelectCreator("success_redirection_url")
export const successRedirectId = campaignSelectCreator("success_redirection_id")
export const summaryDisplayOptionType = campaignSelectCreator("summary_display_option_type")
export const targetingType = campaignSelectCreator("targeting_type")
export const targetsDistrictOffice = campaignSelectCreator("targets_district_office")
export const thankYouMessage = campaignSelectCreator("thank_you_message")
export const vanityUrlSlug = campaignSelectCreator("vanity_url_slug")
export const valid = campaignSelectCreator("valid")

export const customTarget = campaignSelectCreator("custom_target")
export const usesDefaultMemberList = campaignSelectCreator("uses_default_member_list")
export const backgroundImage = campaignSelectCreator("background_image")
export const textKeyword = campaignSelectCreator("text_keyword")
export const interactionTypes = campaignSelectCreator("interaction_types")
export const customInteractionTypes = campaignSelectCreator("custom_interaction_types")
export const relationshipTypes = campaignSelectCreator("relationship_types")
export const customCallToActionText = campaignSelectCreator("custom_call_to_action_text")
export const customCallToActionButtonText = campaignSelectCreator("custom_call_to_action_button_text")
export const requiredVideoLink = campaignSelectCreator("required_video_link")
export const customFields = campaignSelectCreator("custom_fields")
export const phoneNumber = createSelector(
    campaignSelectCreator("phone_number"),
    (phoneNumberObj) => phoneNumberObj && phoneNumberObj.get("associated_number"),
)
export const gamificationPointsMode = campaignSelectCreator("gamification_points_mode")
const customGamificationPoints = campaignSelectCreator("custom_gamification_points")

export const selectIsDraftCampaign = createSelector(
    status,
    (campaignStatus) => campaignStatus === DjangIO.app.grassroots.campaign.types.CampaignStatus.draft.value,
)

export const selectIsP2ACampaign = createSelector(isP2A, (isP2A) => isP2A)

const selectLimitedToTeams = createSelector(
    campaignSelectCreator("limited_to_teams"),
    (limitedToTeams) => limitedToTeams || Immutable.fromJS([]),
)

// Return an array of Team URIS for the CampaignForm
export const selectLimitedToTeamsForCampaignForm = createSelector(selectLimitedToTeams, (limitedToTeamsList) =>
    limitedToTeamsList.toJS(),
)

export const selectRegistrationPageId = createSelector(registrationPage, (regPage) =>
    regPage ? parseInt(DjangIO.app.grassroots.models.GrassrootsRegistrationPage.idFromResourceUri(regPage)) : undefined,
)

export const selectTaggedBills = createIdsFromResourceSelector(
    campaignSelectCreator("tagged_bills"),
    DjangIO.app.bill.models.Bill,
)

export const selectTaggedRegulations = createIdsFromResourceSelector(
    campaignSelectCreator("tagged_regulations"),
    DjangIO.app.reg.models.Regulation,
)

// Props needed for CampaignProfile
export const completed = campaignSelectCreator("completed")
const actionCenterDict = campaignSelectCreator("action_center_dict")

export const actionCenterDomain = createSelector(actionCenterDict, (acs) =>
    acs ? acs.get("domain") : Userdata.subdomain,
)

export const selectActionCenterIdForCampaignProfile = createSelector(actionCenterDict, (acs) => acs && acs.get("id"))

export const actionCenterExternalInteractionTypes = createSelector(
    actionCenterDict,
    (acs) => acs && acs.get("external_interaction_types", Immutable.fromJS([])),
)

export const actionCenterExternalRelationshipTypes = createSelector(
    actionCenterDict,
    (acs) => acs && acs.get("external_relationship_types", Immutable.fromJS([])),
)

export const actionCenterDefaultRegistrationPageUri = createSelector(
    actionCenterDict,
    (acs) =>
        acs &&
        DjangIO.app.grassroots.models.GrassrootsRegistrationPage.resourceUriFromId(
            acs.get("default_registration_page_id"),
        ),
)

// Props needed from campaign to build other selectors
export const primaryTargets = campaignSelectCreator("primary_targets")
export const primaryTargetsSavedSearches = campaignSelectCreator("primary_targets_saved_searches")
export const participants = campaignSelectCreator("participants")
export const participantsSavedSearches = campaignSelectCreator("participants_saved_searches")

const combinedCampaignUris = campaignSelectCreator("combined_campaigns")

const combinedCampaignIds = createSelector(combinedCampaignUris, (uris) =>
    uris.map((uri) => parseInt(DjangIO.app.grassroots.campaign.models.Campaign.idFromResourceUri(uri), 10)).toJS(),
)

export const campaignSelectObjectCreator = (key) =>
    createSelector(getCampaign, (campaign) => campaign && campaign.get(key).toJS())

// Props that are Immutable objects needed from campaign for CampaignForm
export const projects = campaignSelectObjectCreator("projects")
export const projectNames = campaignSelectObjectCreator("project_names")
export const selectedSocialMediaOptions = campaignSelectObjectCreator("social_media_platforms")

// this is a workaround, not clear why this selector is receiving sometimes a list of objects, and sometimes
// a list of strings
const parseProject = (project) => (typeof project === "object" ? project.get("resource_uri") : project)

export const selectCampaignPropIdsFromResourceList = (key, resource) =>
    createSelector(getCampaign, (campaign) => {
        const uris = campaign.get(key)
        return uris.map((uri) => parseInt(resource.idFromResourceUri(parseProject(uri), 10))).toJS()
    })

export const projectIds = createSelector(getCampaign, (campaign) => {
    const uris = campaign.get("projects")
    return uris
        .map(
            (uri) =>
                parseInt(DjangIO.app.userdata.models.IssueManagement.idFromResourceUri(parseProject(uri), 10)) ||
                parseInt(DjangIO.app.userdata.models.Project.idFromResourceUri(parseProject(uri), 10)),
        )
        .toJS()
})

// Confirmation Email Selectors
export const selectEmailTemplateUri = campaignSelectCreator("email_template")

export const selectEmailTemplateId = createSelector(selectEmailTemplateUri, (uri) => uri && idFromResourceUri(uri))

export const selectShouldSendConfirmationEmail = campaignStateSelectCreator("shouldSendConfirmationEmail")

export const isCampaignTypeSelectCreator = (campaignTypeGiven) =>
    createSelector(
        campaignType,
        (type) => type === DjangIO.app.grassroots.campaign.types.CampaignType[campaignTypeGiven].value,
    )

export const confirmationEmailSubject = campaignSelectCreator("conf_email_subject")
export const confirmationEmailFromName = campaignSelectCreator("conf_email_from_name")
export const confirmationEmailReplyToName = campaignSelectCreator("conf_email_reply_to_name")

export const confirmationEmailFromAddressUsername = campaignStateSelectCreator("confirmationEmailFromAddressUsername")
export const confirmationEmailFromAddressDomain = campaignStateSelectCreator("confirmationEmailFromAddressDomain")
export const confirmationEmailReplyToAddressUsername = campaignStateSelectCreator(
    "confirmationEmailReplyToAddressUsername",
)
export const confirmationEmailReplyToAddressDomain = campaignStateSelectCreator("confirmationEmailReplyToAddressDomain")

export const defaultTargetsSavedSearch = createSelector(getCampaign, (campaign) => {
    if (campaign.get("default_targets_saved_search")) {
        return campaign.get("default_targets_saved_search").toJS()
    }
})

export const targetsSinglePerson = createSelector(
    primaryTargets,
    primaryTargetsSavedSearches,
    usesDefaultMemberList,
    (primaryTargets, primaryTargetsSavedSearches, usesDefaultMemberList) => {
        // campaign has to be loaded
        if (primaryTargets) {
            // make sure there less than 1 person selected and we are not
            // using the default member list
            return primaryTargetsSavedSearches.size === 0 && primaryTargets.size <= 1 && !usesDefaultMemberList
        }
    },
)

export const selectGroup = (groupName, savedSearch) =>
    createSelector(getCampaign, (campaign) => {
        let selection = []
        // make sure we're done loading and key exists
        if (campaign.get(groupName)) {
            // load in the individuals and attach the saved searches
            selection = campaign.get(groupName).toJS().concat(campaign.get(savedSearch).toJS())
        }
        return selection
    })

// More props that select groups out of saved searches and Person Objects for CampaignForm
const primaryTargetsAndSearches = campaignSelectObjectCreator("primary_targets_saved_searches")
const secondaryTargetsAndSearches = selectGroup("secondary_targets", "secondary_targets_saved_searches")
const participantsAndSearches = selectGroup("participants", "participants_saved_searches")

export const primaryCombinedIds = createSelector(primaryTargetsSavedSearches, (primarySavedSearches) => {
    let primary = []
    if (primarySavedSearches) {
        primarySavedSearches.toJS().map((item) => {
            primary.push(DjangIO.app.userdata.models.SafedSearch.idFromResourceUri(item.resource_uri))
        })
    }
    return primary
})

const canEditLetter = createSelector(draftRequirements, (enumValue) => {
    return enumValue === DjangIO.app.grassroots.campaign.types.DraftOptions.can_edit.value
})

export const partialEditing = createSelector(
    draftRequirements,
    (enumValue) => enumValue === DjangIO.app.grassroots.campaign.types.DraftOptions.partial_edit.value,
)

//  NEW PLACEHOLDERS
export const selectIsLoadingPlaceholders = campaignStateSelectCreator("isLoadingPlaceholders")
export const selectPlaceholders = createToJsSelector(campaignStateSelectCreator("placeholders"))
// function to select only the placeholders for contacts
export const selectContactPlaceholders = createSelector(selectPlaceholders, (allPlaceholders) => {
    const { grassroots_contact } = DjangIO.app.userdata.outbox.types.PlaceholderType
    return allPlaceholders.filter(
        (placeholderCategory) =>
            placeholderCategory &&
            // if it is a number, compare equality, if it is a grouped enum,
            // check that it is contained in the valid_for field
            (typeof placeholderCategory.valid_for === "number"
                ? placeholderCategory.valid_for === grassroots_contact.value
                : placeholderCategory.valid_for.includes(grassroots_contact.value)),
    )
})

// select all the text fields that could possible use a placeholder and concat
// them to send to the backend
export const selectTextToValidate = createSelector(
    shortDescription,
    longDescription,
    thankYouMessage,
    posSubject,
    posMessage,
    negSubject,
    negMessage,
    multiMessages,
    (
        shortDescription,
        longDescription,
        thankYouMessage,
        posSubject,
        posMessage,
        negSubject,
        negMessage,
        multiMessages,
    ) => {
        const allText = [
            shortDescription,
            longDescription,
            thankYouMessage,
            posSubject,
            posMessage,
            negSubject,
            negMessage,
            ...multiMessages,
        ].reduce((acc, text) => (text ? acc + text : acc), "")
        return allText
    },
)

export const placeholderSelectors = {
    contactPlaceholders: selectContactPlaceholders,
    allPlaceholders: selectPlaceholders,
}

export const selectNoteCustomFields = createSelector(
    selectCustomFields(DjangIO.app.models.QuorumDataType.note.value),
    (noteCustomFields) =>
        noteCustomFields.map((customField) => ({
            value: customField.resource_uri,
            label: customField.name,
        })),
)

export const redirectThankYouPage = createSelector(
    postSubmissionAction,
    (actionType) =>
        actionType === DjangIO.app.grassroots.campaign.types.CampaignPostSubmissionActionType.thank_you.value,
)

export const redirectAnotherCampaign = createSelector(
    postSubmissionAction,
    (actionType) =>
        actionType === DjangIO.app.grassroots.campaign.types.CampaignPostSubmissionActionType.another_campaign.value,
)

export const redirectExternalLink = createSelector(
    postSubmissionAction,
    (actionType) =>
        actionType === DjangIO.app.grassroots.campaign.types.CampaignPostSubmissionActionType.external_link.value,
)

export const redirectDonationForm = createSelector(
    postSubmissionAction,
    (actionType) =>
        actionType === DjangIO.app.grassroots.campaign.types.CampaignPostSubmissionActionType.donation_form.value,
)

export const selectActionCenterIdFromRouteOrProp = (_, props) =>
    props && (props.actionCenterId || props.params?.actionCenterId)

export const selectGrassrootsRegistrationFormFieldLabel = createSelector(
    isCampaignTypeSelectCreator("donate"),
    (isDonateCampaign) => `${isDonateCampaign ? "Donation" : "Registration"} Form`,
)

export const selectCampaignPostSubmissionChoices = () =>
    DjangIO.app.grassroots.campaign.types.CampaignPostSubmissionActionType.items()
        .filter(
            (item) =>
                item.value !==
                    DjangIO.app.grassroots.campaign.types.CampaignPostSubmissionActionType.donation_form.value ||
                isFeatureEnabled("ff_pac"),
        )
        .sort(DjangIO.valueSort)

export const selectTargetMembers = createSelector(
    defaultTargetsSavedSearch,
    (targets) => targets && targets.filters && targets.filters[0].ph_minor_role_type, // e.g. [1, 2, 3]
)

export const shouldDisplayCustomCTAButtonField = createSelector(
    isCampaignTypeSelectCreator("petition"),
    isCampaignTypeSelectCreator("gotv"),
    (isPetition, isGotv) => isPetition || isGotv,
)

export const selectShouldDisplayCampaignTargetingField = createSelector(
    hasCustomTarget,
    isCampaignTypeSelectCreator("call"),
    isCampaignTypeSelectCreator("tweet_at_member"),
    isCampaignTypeSelectCreator("visit"),
    isCampaignTypeSelectCreator("write_member"),
    (campaignHasCustomTarget, isCallCampaign, isTweetCampaign, isVisitCampaign, isWriteCampaign) =>
        !campaignHasCustomTarget && (isCallCampaign || isTweetCampaign || isVisitCampaign || isWriteCampaign),
)

const policyArea = campaignSelectCreator("policy_area")

export const selectInvalidSingleBodyCampaign = createSelector(
    getCampaign,
    sameDraft,
    (campaign, sameDraft) =>
        // The problem only arises for write campaigns where supporters see a single body for all messages...
        campaign.get("campaign_type") === DjangIO.app.grassroots.campaign.types.CampaignType.write_member.value &&
        campaign.get("single_body_for_all_messages") &&
        // Placeholders can be placed in the subject or the body
        (targetPlaceholderRegex.test(campaign.get("pos_message")) ||
            targetPlaceholderRegex.test(campaign.get("pos_subject")) ||
            // And for campaigns with a positive and negative group of recipients, those must also be checked
            (!sameDraft &&
                (targetPlaceholderRegex.test(campaign.get("neg_message")) ||
                    targetPlaceholderRegex.test(campaign.get("neg_subject"))))),
)

export const selectRenderNavBarTransparentToggleFullBleedHeader = createSelector(
    fullBleedHeaderType,
    navigationBarType,
    (fullBleedHeaderTypeProp, navigationBarTypeProp) =>
        (fullBleedHeaderTypeProp === DjangIO.app.grassroots.campaign.types.CampaignFullBleedHeaderType.image.value ||
            fullBleedHeaderTypeProp ===
                DjangIO.app.grassroots.campaign.types.CampaignFullBleedHeaderType.video.value) &&
        navigationBarTypeProp !== DjangIO.app.grassroots.campaign.types.CampaignNavigationBarType.none.value,
)

export const selectRenderNavBarTransparentToggleBackground = createSelector(
    backgroundStyleType,
    selectRenderNavBarTransparentToggleFullBleedHeader,
    navigationBarType,
    (backgroundStyleTypeProp, toggleAlreadyRendered, navigationBarTypeProp) =>
        backgroundStyleTypeProp === DjangIO.app.grassroots.enums.ActionCenterBackgroundStyle.image.value &&
        !toggleAlreadyRendered &&
        navigationBarTypeProp !== DjangIO.app.grassroots.campaign.types.CampaignNavigationBarType.none.value,
)

export const selectRenderIsOneClickRegistrationEnabled = createSelector(campaignType, (value) =>
    Boolean(value && DjangIO.app.grassroots.campaign.types.CampaignType.by_value(value).allow_one_click_registration),
)

export const campaignFormSelectorsObj = {
    actionCenter,
    actionCenterDomain,
    actionCenterId: selectActionCenterIdFromRouteOrProp,
    actionCenterExternalInteractionTypes,
    actionCenterExternalRelationshipTypes,
    actionCenterDefaultRegistrationPageUri,
    activated,
    allowSupportersToTargetAllTargets,
    alwaysShowToAdvocates,
    backgroundColorOverride,
    backgroundColorPickerState,
    // image which appears in the campaign column
    backgroundImage,
    // image which may be used as a background for the page where the campaign is displayed in action center
    backgroundImageOverrideUrl,
    backgroundStyleType,
    campaign: getCampaign,
    campaignPostSubmissionChoices: selectCampaignPostSubmissionChoices,
    campaignType,
    canEditLetter,
    combinedCampaignIds,
    confirmationEmailSubject,
    confirmationEmailFromName,
    confirmationEmailFromAddressUsername,
    confirmationEmailFromAddressDomain,
    confirmationEmailReplyToName,
    confirmationEmailReplyToAddressUsername,
    confirmationEmailReplyToAddressDomain,
    defaultTargetsSavedSearch,
    draftMessage,
    draftRequirements,
    editingCampaign,
    emailTemplateId: selectEmailTemplateId,
    fixedPreMessage,
    fixedPostMessage,
    fixedPreMessageTwo,
    fixedPostMessageTwo,
    footerHtml,
    fullBleedHeaderImageContain,
    fullBleedHeaderImageUrl,
    fullBleedHeaderVideoUrl,
    fullBleedHeaderType,
    grassrootsRegistrationFormFieldLabel: selectGrassrootsRegistrationFormFieldLabel,
    commentOrganizationType,
    submittingOrganizationName,
    id,
    inboundTextingReplyMessage: campaignSelectCreator("inbound_texting_reply_message"),
    internalOnlyName,
    isCallCampaign: isCampaignTypeSelectCreator("call"),
    isCombinedCampaign: isCampaignTypeSelectCreator("combined_campaign"),
    isCommentCampaign: isCampaignTypeSelectCreator("comment_on_regulation"),
    isDonateCampaign: isCampaignTypeSelectCreator("donate"),
    isGotvCampaign: isCampaignTypeSelectCreator("gotv"),
    isLimitedToTeamsEnabled: selectIsLimitedToTeamsEnabled,
    isLogInteractionCampaign: isCampaignTypeSelectCreator("log_interaction"),
    isLogRelationshipCampaign: isCampaignTypeSelectCreator("log_relationship"),
    isPetitionCampaign: isCampaignTypeSelectCreator("petition"),
    isShareCampaign: isCampaignTypeSelectCreator("share"),
    isStoryCampaign: isCampaignTypeSelectCreator("grassroots_story"),
    isTweetCampaign: isCampaignTypeSelectCreator("tweet_at_member"),
    isVisitCampaign: isCampaignTypeSelectCreator("visit"),
    isWhiteHouseCampaign: isCampaignTypeSelectCreator("write_white_house"),
    isWriteCampaign: isCampaignTypeSelectCreator("write_member"),
    isFullBleedHeaderTextLeftAligned,
    isOneClickRegistrationEnabled,
    limitsParticipants,
    loadingCampaign,
    longDescription,
    multipleActionsAllowed,
    campaignSharingAllowed,
    name,
    navigationBarIsTransparent,
    navigationBarType,
    negMessage,
    negSubject,
    orgDesignUri: campaignSelectCreator("organization_design"),
    pageLayout,
    descriptionAlignment,
    posMessage,
    posSubject,
    multiMessages,
    priority,
    projectIds,
    projects,
    postSubmissionAction,
    redirectAnotherCampaign,
    redirectDonationForm,
    redirectExternalLink,
    redirectThankYouPage,
    registrationPage,
    registrationPageId: selectRegistrationPageId,
    regulationId,
    requireConfirmation,
    renderIsOneClickRegistrationEnabled: selectRenderIsOneClickRegistrationEnabled,
    renderNavBarTransparentToggleFullBleedHeader: selectRenderNavBarTransparentToggleFullBleedHeader,
    renderNavBarTransparentToggleBackground: selectRenderNavBarTransparentToggleBackground,
    requiredVideoLink,
    sameDraft,
    selectedSocialMediaOptions,
    shareUrl,
    shortDescription,
    shouldUseFooterHtml,
    showNumActions,
    showNumSupporters,
    shouldSendConfirmationEmail: selectShouldSendConfirmationEmail,
    shouldDisplayCampaignTargetingField: selectShouldDisplayCampaignTargetingField,
    shouldDisplayCustomCTAButtonField,
    singleBodyForAllMessages,
    status,
    successRedirectId,
    successRedirectUrl,
    supportersCanChooseTargets,
    summaryDisplayOptionType,
    taggedBills: selectTaggedBills,
    taggedRegulations: selectTaggedRegulations,
    targetingType,
    targetsDistrictOffice,
    targetsSinglePerson,
    textKeyword,
    phoneNumber,
    thankYouMessage,
    vanityUrlSlug,
    valid,
    customTarget,
    hasCustomTarget,
    usesDefaultMemberList,
    interactionTypes,
    customInteractionTypes,
    relationshipTypes,
    customCallToActionText,
    customCallToActionButtonText,
    customFields,
    gamificationPointsMode,
    customGamificationPoints,
    customJs: campaignSelectCreator("custom_javascript"),
    customCss: campaignSelectCreator("custom_css"),
    customThankYouJs: campaignSelectCreator("custom_thank_you_javascript"),

    partialEditing,
    primaryCombinedIds,
    primaryTargetsAndSearches,
    policyArea,
    // primarySearchIds,
    // primaryPersonIds,
    secondaryTargetsAndSearches,
    participantsAndSearches,

    outboxDomains: selectOUtboxDomainsForComponent,
    singleOutboxDomain: selectSingleOutboxDomain,

    // placeholders
    ...placeholderSelectors,

    socialTitle: campaignSelectCreator("social_title"),
    socialShareText: campaignSelectCreator("social_share_text"),
    socialDescription: campaignSelectCreator("social_description"),
    socialImageUrl: campaignSelectCreator("social_image_url"),

    noteCustomFields: selectNoteCustomFields,

    invalidSingleBodyCampaign: selectInvalidSingleBodyCampaign,
}

export const campaignFormSelectors = createStructuredSelector(campaignFormSelectorsObj)

/**** Campaign Profile Selectors *********/
const campaignTypeSelectCreator = (key) =>
    createSelector(campaignType, (typeEnum) =>
        typeEnum ? DjangIO.app.grassroots.campaign.types.CampaignType.by_value(typeEnum)[key] : "",
    )

export const campaignTypeLabel = campaignTypeSelectCreator("label")
export const campaignTypeIcon = campaignTypeSelectCreator("icon")

export const selectHasCustomSupporters = createSelector(
    participantsSavedSearches,
    participants,
    (participantsSavedSearches, participants) => {
        if (participantsSavedSearches) {
            return participantsSavedSearches.size && participants.size
        }
        return false
    },
)

export const campaignProfileHeaderSelectors = createStructuredSelector({
    activated,
    completed,
    id,
    name,
    status,
    campaignTypeLabel,
    campaignTypeIcon,
    projectNames,
})

// Additional props needed for CampaignProfileTopLevelContainer
export const numberActions = campaignSelectCreator("number_actions_count")
export const numberActionsLastMonth = campaignSelectCreator("number_actions_last_month_count")
export const numberPageVisits = campaignSelectCreator("number_page_visits")
export const numberPageVisitsLastMonth = campaignSelectCreator("number_page_visits_last_month")
export const numberParticipants = campaignSelectCreator("number_participants_count")
export const numberParticipantsLastMonth = campaignSelectCreator("number_participants_last_month_count")

export const campaignProfileOverviewSelectors = createStructuredSelector({
    id,
    campaignType,
    numberActions,
    numberActionsLastMonth,
    numberParticipants,
    numberParticipantsLastMonth,
})

export const selectCampaignPreviewPath = createSelector(
    id,
    vanityUrlSlug,
    selectIsDraftCampaign,
    (id, vanityUrlSlug, isDraftCampaign) => {
        const { GrassrootsWidgetType } = DjangIO.app.grassroots.types
        return `${
            isDraftCampaign ? GrassrootsWidgetType.draft_campaign.widget_url : GrassrootsWidgetType.campaign.widget_url
        }${vanityUrlSlug || id}`
    },
)

export const selectShouldShowDownloadWord = createSelector(
    campaignType,
    (campaignType) =>
        campaignType && campaignType === DjangIO.app.grassroots.campaign.types.CampaignType.petition.value,
)

export const selectShouldShowDownloadTargetReport = createSelector(
    campaignType,
    (campaignType) =>
        campaignType && DjangIO.app.grassroots.campaign.types.CampaignType.by_value(campaignType).targets_members,
)

export const campaignProfileActionsConnection = createStructuredSelector({
    name,
    id,
    campaignType,
    domain: actionCenterDomain,
    isDraftCampaign: selectIsDraftCampaign,
    isP2A: selectIsP2ACampaign,
    campaignPreviewPath: selectCampaignPreviewPath,
    shouldShowDowloadWord: selectShouldShowDownloadWord,
    shouldShowDownloadTargetReport: selectShouldShowDownloadTargetReport,
})

export const advocacySelectors = createStructuredSelector({
    campaignStatusFilter,
})

// profiles-new
export const selectCampaignCustomDescription = selectSpecificCustomDescription(
    DjangIO.app.models.QuorumDataType.campaign.key,
)

const makeCampaignDescriptionSelect = makeSelectFieldFromCustomDescription(selectCampaignCustomDescription)

export const selectCurrentTab = makeCampaignDescriptionSelect("currentTab")
export const selectIsTruncated = makeCampaignDescriptionSelect("titleIsTruncated")

const { CampaignStatus } = DjangIO.app.grassroots.campaign.types

export const projectCreatedDate = (activated, completed, status) => {
    // active: show start date
    if (activated && status === CampaignStatus.active.value) {
        const activatedDateTime = new Date(activated)
        const day = activatedDateTime.getDate()
        const month = activatedDateTime.getMonth() + 1
        return ` began on ${month}/${day}`
    }
    // completed: show end date
    else if (completed && status === CampaignStatus.complete.value) {
        const completedDateTime = new Date(completed)
        const day = completedDateTime.getDate()
        const month = completedDateTime.getMonth() + 1
        return ` ended on ${month}/${day}`
    }
    // draft or no dates captured: show no date
    return ""
}

export const selectHeaderSubtitle = createSelector(
    [campaignTypeLabel, activated, completed, status],
    (label, activated, completed, status) => {
        return `${label} Campaign${projectCreatedDate(activated, completed, status)}`
    },
)

export const selectHeaderTitlePrefix = () => DjangIO.app.models.QuorumDataType.campaign.singular

export const selectProjectNamesList = createSelector(
    projectNames,
    (projectNames) => projectNames && projectNames.reduce((acc, name) => (acc ? `${acc}, ${name}` : name), ""),
)

export const selectActionCenterName = createSelector(
    actionCenterDict,
    (actionCenterDict) => actionCenterDict && actionCenterDict.get("name"),
)

export const selectHeaderSubtitleForWeb = createSelector(
    selectHeaderSubtitle,
    selectProjectNamesList,
    selectActionCenterName,
    textKeyword,
    (headerSubtitle, projectNames, actionCenterName, textKeyword) => {
        return `${headerSubtitle}${actionCenterName ? ` | ${actionCenterName}` : ""}${
            projectNames ? ` | ${projectNames}` : ""
        }${textKeyword ? ` | Texting Keyword: ${textKeyword}` : ""}`
    },
)

export const selectIsLoadingCampaign = campaignStateSelectCreator("isLoadingCampaign")

export const shouldRenderDeleteButton = createSelector(selectIsDraftCampaign, (isDraftCampaign) => isDraftCampaign)
export const shouldRenderEditButton = createSelector(selectIsP2ACampaign, (isP2ACampaign) => !isP2ACampaign)

export const selectCompletedTimeString = createSelector(
    completed,
    selectIsDraftCampaign,
    (completed, isDraftCampaign) => {
        // if this is a draft, it cannot have a completed time
        if (isDraftCampaign) {
            return "N/A"
        }

        // not a draft, so if there's no completed time, it's still ongoing
        return completed ? parseDateTime(completed) : "still active"
    },
)

export const selectCampaignProfileAboutSectionData = createSelector(
    name,
    selectIsTruncated,
    campaignTypeLabel,
    activated,
    selectActionCenterName,
    textKeyword,
    selectProjectNamesList,
    selectCompletedTimeString,
    (
        campaignName,
        titleIsTruncated,
        campaignTypeLabel,
        activated,
        actionCenterName,
        textKeyword,
        projectNames,
        completedTime,
    ) => {
        return [
            { title: "Campaign Name", value: titleIsTruncated && campaignName, dataItem: DataItem },
            { title: "Campaign Type", value: campaignTypeLabel, dataItem: DataItem },
            { title: "Started", value: activated ? parseDateTime(activated) : "N/A", dataItem: DataItem },
            { title: "Ended", value: completedTime, dataItem: DataItem },
            { title: "Action Center", value: actionCenterName, dataItem: DataItem },
            { title: "Text Keyword", value: textKeyword || "N/A", dataItem: DataItem },
            { title: "Issues Tied To", value: projectNames, dataItem: DataItem },
        ]
    },
)
